import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Unstable_Grid2';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { debounce } from 'lodash';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import ClientCard from '../../components/client/card';
import ClientEdit from '../../components/client/edit';
import ClientMove from '../../components/client/move';
import TrainerAssign from '../../components/client/reassign';
import request, {
  createClient,
  updateClient,
  reassignClient,
  moveClient,
} from '../../apis/thrivePerformance/v1/requests.mjs';
import useGetClients from '../../apis/thrivePerformance/v1/clients/getClients';
import useGetTrainers from '../../apis/thrivePerformance/v1/user/getTrainers';
import useGetSites from '../../apis/thrivePerformance/v1/sites/getSites';

export default function Clients() {
  const { siteId } = useParams();
  const [setSnackbar, loginState, setLoginState, auth, userState, setUserState, user] = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [clientEditDialogOpen, setClientEditDialogOpen] = useState(false);
  const [clientCreateDialogOpen, setClientCreateDialogOpen] = useState(false);
  const [trainerAssignDialogOpen, setTrainerAssignDialogOpen] = useState(false);
  const [clientMoveDialogOpen, setClientMoveDialogOpen] = useState(false);
  const [client, setClient] = useState({});
  const [processing, setProcessing] = useState(false);
  const [params, setParams] = useState({});
  const [clients, error, refreshClients] = useGetClients(auth, params, siteId);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [trainerParams, setTrainerParams] = useState({siteId: siteId});
  const [trainers, trainersError, refreshTrainers] = useGetTrainers(auth, trainerParams);
  const [sites, sitesError, refreshSites] = useGetSites(auth);

  function handleClientCreate() {
    setClient({});
    setClientCreateDialogOpen(true);
  }

  function handleClientEdit(client) {
    setClient(client);
    setClientEditDialogOpen(true);
  }

  function handleDialogClose() {
    setClientCreateDialogOpen(false);
    setClientEditDialogOpen(false);
    setClientMoveDialogOpen(false);
  }

  async function handleCreateClient(client) {
    setProcessing(true);
    try {
      await request(createClient(auth, client));
      handleDialogClose();
      refreshClients();
      setSnackbar('success', 'The client was created successfully.');
    } catch (err) {
      console.log(err);
      setSnackbar('error', 'An error occurred while attempting to create the client.');
    }
    setProcessing(false);
  }

  async function handleUpdateClient(client) {
    setProcessing(true);
    try {
      await request(updateClient(auth, client));
      handleDialogClose();
      refreshClients();
      setSnackbar('success', 'The client information was updated successfully.');
    } catch (err) {
      console.log(err);
      setSnackbar('error', 'An error occurred while attempting to update the client information.');
    }
    setProcessing(false);
  }

  async function handleClientReassign(trainerId) {
    setProcessing(true);
    try {
      await request(reassignClient(auth, client.clientId, trainerId, siteId));
      setTrainerAssignDialogOpen(false);
      refreshClients();
      setSnackbar('success', 'The client was reassigned successfully.');
    } catch (err) {
      console.log(err);
      setSnackbar('error', 'An error occurred while attempting to reassign the client.');
    }
    setProcessing(false);
  }

  async function handleClientMove(toSiteId) {
    setProcessing(true);
    try {
      await request(moveClient(auth, client.clientId, siteId, toSiteId));
      setClientMoveDialogOpen(false);
      refreshClients();
      setSnackbar('success', 'The client was moved successfully.');
    } catch (err) {
      console.log(err);
      setSnackbar('error', 'An error occurred while attempting to move the client.');
    }
    setProcessing(false);
  }

  function handleReassignDialogOpen(client) {
    setClient(client);
    setTrainerAssignDialogOpen(true);
  }

  function handleClientMoveDialogOpen(client) {
    setClient(client);
    setClientMoveDialogOpen(true);
  }

  const debouncedSearch = useCallback(
    debounce(async (searchValue) => {
      setLoading(true);
      try {
        setParams({
          searchTerm: searchValue,
        });
        refreshClients();
      } catch (err) {
        console.log(err);
        setSnackbar('error', 'An error occurred while attempting to retrieve the clients.');
      }
      setLoading(false);
    }, 1000),
    []
  );

  useEffect(() => {
    if (error) {
      if (loading) {
        setLoading(false);
      }
      setSnackbar('error', error.error);
    }
  }, [clients, error]);

  const handlePageChange = (event, value) => {
    setParams({
      ...params,
      ...{ startFrom: (value - 1) * clients.limit },
    });
    setPage(value);
  };

  const totalPages =
    clients && clients.totalClients
      ? Math.floor((clients.totalClients - 1) / clients.limit) + 1
      : 1;

  return (
    <>
      <Stack direction="row" spacing={2}>
        <Typography variant="h3" component="h3" sx={{ mb: 2 }}>
          Clients
        </Typography>
        <IconButton size="large" color="primary" onClick={handleClientCreate}>
          <AddIcon />
        </IconButton>
      </Stack>
      <Stack direction="row" spacing={2} sx={{ mt: 2}}>
        <TextField
          id="filled-search"
          label="Search Clients"
          type="search"
          variant="filled"
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            debouncedSearch(event.target.value);
          }}
        />
        <IconButton size="large" color="primary">
          <SearchIcon />
        </IconButton>
      </Stack>
      <Grid container gap={2} spacing={2} sx={{ mt: 2 }}>
        {clients &&
          clients.clients &&
          clients.clients.map((client) => {
            return (
              <Grid xs={12} sm={6} lg={4} key={client.clientId}>
                <ClientCard
                  client={client}
                  handleEdit={handleClientEdit}
                  handleReassign={handleReassignDialogOpen}
                  handleMove={handleClientMoveDialogOpen}
                  locale={user.locale}
                  timeZone={user.zoneinfo}
                  auth={auth}
                />
              </Grid>
            );
          })}
        {!clients && loading ? (
          <Grid>
            <CircularProgress color="primary" />
          </Grid>
        ) : null}
      </Grid>
      {totalPages > 1 && (
        <Grid container spacing={2} direction="column" alignItems="center">
          <Grid>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              size="large"
              color="primary"
            />
          </Grid>
        </Grid>
      )}
      <Dialog open={clientEditDialogOpen} onClose={handleDialogClose} fullWidth>
        <DialogTitle>Client Information</DialogTitle>
        <DialogContent>
          <ClientEdit
            client={client}
            processing={processing}
            handleUpdate={handleUpdateClient}
            handleCancel={handleDialogClose}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={clientCreateDialogOpen} onClose={handleDialogClose} fullWidth>
        <DialogTitle>New Client</DialogTitle>
        <DialogContent>
          <ClientEdit
            client={client}
            processing={processing}
            handleUpdate={handleCreateClient}
            handleCancel={handleDialogClose}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={trainerAssignDialogOpen} onClose={handleDialogClose} fullWidth>
        <DialogTitle>Trainer Assignment</DialogTitle>
        <DialogContent>
          <TrainerAssign
            client={client}
            trainers={trainers}
            processing={processing}
            handleReassign={handleClientReassign}
            handleCancel={handleDialogClose}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={clientMoveDialogOpen} onClose={handleDialogClose} fullWidth>
        <DialogTitle>Move Client</DialogTitle>
        <DialogContent>
          <ClientMove
            client={client}
            sites={sites?.sites || []}
            processing={processing}
            handleMove={handleClientMove}
            handleCancel={handleDialogClose}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
